import React, { Component, forwardRef, Fragment } from "react";
import DatePicker from "react-datepicker";
import { Redirect, withRouter } from "react-router-dom";
import moment from "moment";
import {
  Alert,
  Button,
  Dropdown,
  Row,
  Col,
  Table,
  InputGroup,
} from "react-bootstrap";
import { years, months, isWeekday } from "../../../utils/CalendarHelpers";
import { getMonth, getYear } from "date-fns";
import { get, destroy } from "../../../utils/DeApi";
import { exportDocuments } from "../../../utils/exportUtils";

import Loader from "../../Loader/Loader";
import ErrorHandler from "../../ErrorHandler/ErrorHandler";
import FilingPreview from "../../Filing/FilingPreview/FilingPreview";
import { orderBy } from "lodash";
import EntitiesExtraction from "../../Extraction/EntitiesExtraction/EntitiesExtraction";
import { getNameFromEmail } from "../../../utils/StringUtils";
import "./CatalogueReview.scss";

class CatalogueReview extends Component {
  constructor(props) {
    super(props);
    this.subscribedPromises = [];
    this.state = {
      filings: [],
      catalogue: [],
      selections: [],
      selectedDocs: [],
      selectedFilter: "filedAt",
      sortColumnMapping: {
        Company: { column: "filing.companyNameLong", direction: "desc" },
        CIK: { column: "filing.cik", direction: "desc" },
        "Form Type": { column: "filing.formType", direction: "desc" },
        "Filed At": { column: "filing.filedAt", direction: "desc" },
        Analyst: { column: "email", direction: "desc" },
        "Filing Date": { column: "filing.filedAt", direction: "desc" },
      },
      sortBy: "",
      sortSelection: {},
    };
  }

  showDocument(filing, doc) {
    this.setState({ preview: { filing: filing, doc: doc } });
  }

  hideDocument() {
    this.setState({ preview: null });
  }

  componentDidMount() {
    this.fetchCatalogue();
    this.fetchInventories();
    this.fetchSelections();
  }

  componentWillUnmount() {
    this.subscribedPromises.forEach(function (promise) {
      promise.cancel();
    });
  }

  fetchFilings(catalogue) {
    const { filedAt } = this.props;
    if (!catalogue.length) {
      this.setState({ catalogue: [], error: null, isLoading: false });
      return;
    }
    this.setState({ error: null, isLoading: true });

    const accessionNos = catalogue.map(
      (item) => 'accessionNo:"' + item.filingId + '"'
    );

    const filingsQuery = accessionNos.length ? accessionNos.join(" OR ") : "";
    const filingsPromise = get("/search/filings", {
      // when the filter is created At then we will not send filedAt attribute
      params: {
        ...(this.state.selectedFilter === "filedAt" && { filedAt: filedAt }),
        q: filingsQuery,
        offset: 0,
        limit: 2000,
      },
    });
    filingsPromise.promise
      .then((response) => {
        var filings = response.data;
        this.setState({
          catalogue: catalogue
            .map((item) => {
              return {
                ...item,
                ...{
                  filing: filings.find(
                    (filing) => filing.accessionNo === item.filingId
                  ),
                },
              };
            })
            .filter(
              (catalogued) => !!catalogued.filing && !!catalogued.formType
            )
            .sort((a, b) => {
              if (a.email < b.email) {
                return -1;
              }
              if (a.email > b.email) {
                return 1;
              }
              return 0;
            }),
          filingsMeta: response.meta,
          error: null,
          isLoading: false,
        });
      })
      .catch((error) => {
        !error.isCanceled && this.setState({ error: error, isLoading: false });
      });
    this.subscribedPromises.push(filingsPromise);
  }

  fetchCatalogue() {
    const { filedAt, inventory } = this.props;
    this.setState({ error: null, isLoading: true });

    const cataloguePromise = get("/catalogues", {
      params: {
        [this.state.selectedFilter]: filedAt,
        inventories: [inventory],
        fromSection: "catalogue",
      },
    });
    cataloguePromise.promise
      .then((response) => {
        this.fetchFilings(response.data);
      })
      .catch((error) => {
        !error.isCanceled && this.setState({ error: error, isLoading: false });
      });

    this.subscribedPromises.push(cataloguePromise);
  }

  fetchSelections() {
    const { filedAt } = this.props;
    this.setState({ error: null, isLoading: true });

    const searchFilingsPromise = get("/catalogues", {
      params: {
        [this.state.selectedFilter]: filedAt,
        fromSection: "catalogue",
        inventories: [this.props.inventory],
      },
    });
    searchFilingsPromise.promise
      .then((response) => {
        this.fetchFilings(response.data);
      })
      .catch((error) => {
        !error.isCanceled && this.setState({ error: error, isLoading: false });
      });
  }

  fetchInventories() {
    const fetchInventoriesPromise = get("/inventories");

    fetchInventoriesPromise.promise
      .then((response) => {
        this.setState({ inventories: response.data });
      })
      .catch((error) => {
        !error.isCanceled && this.setState({ error: error, isLoading: false });
      });

    this.subscribedPromises.push(fetchInventoriesPromise);
  }

  removeFromInventory(catalogued) {
    const { filedAt, inventory } = this.props;
    this.setState({ error: null, isLoading: true });

    const removePromise = destroy(`/catalogues/${filedAt}/inventory`, {
      params: { documents: [catalogued], inventory: inventory },
    });
    removePromise.promise
      .then((response) => {
        this.setState({ error: null, isLoading: false }, () =>
          this.fetchCatalogue()
        );
      })
      .catch((error) => {
        !error.isCanceled && this.setState({ error: error, isLoading: false });
      });
    this.subscribedPromises.push(removePromise);
  }

  exportFilings() {
    const { catalogue, inventories } = this.state;
    const { filedAt, inventory } = this.props;

    const fileName = `${filedAt} ${
      inventories?.find((item) => item.id === inventory)?.name || "catalogue"
    }.csv`;
    exportDocuments(catalogue, fileName);
  }

  handleSortBy = (title) => {
    let { sortColumnMapping, sortSelection } = this.state;
    sortColumnMapping = {
      ...sortColumnMapping,
      [title]: {
        ...sortColumnMapping[title],
        direction:
          sortColumnMapping[title]?.direction === "asc" ? "desc" : "asc",
      },
    };
    sortSelection = {
      ...sortSelection,
      [title]: sortColumnMapping[title],
    };
    this.setState(
      {
        sortColumnMapping: sortColumnMapping,
        sortBy: title,
        sortSelection: sortSelection,
      },
      () => {
        this.sortData();
      }
    );
  };

  Heading = ({ title, classes = "" }) => {
    const { sortSelection, sortBy } = this.state;
    const direction = sortSelection[title]?.direction;
    const iconAsc = "south";
    const iconAscClass =
      sortBy === title && direction && (direction === "asc" ? "active" : "");
    const iconDesc = "north";
    const iconDescClass =
      sortBy === title && direction && (direction === "desc" ? "active" : "");
    return (
      <th
        className={classes}
        role={"button"}
        onClick={() => this.handleSortBy(title)}
      >
        {title}
        <span className={`material-icons sort ${iconAscClass}`}>{iconAsc}</span>
        <span className={`material-icons sort ${iconDescClass}`}>
          {iconDesc}
        </span>
      </th>
    );
  };

  sortData = () => {
    if (this.state.sortBy !== "") {
      const { sortColumnMapping, catalogue } = this.state;
      let column = sortColumnMapping[this.state.sortBy].column;
      if (column === "filing.cik") {
        column = (obj) => Number(obj.filing.cik);
      }
      const direction = sortColumnMapping[this.state.sortBy]?.direction;
      this.setState({
        catalogue: orderBy(catalogue, column, direction),
      });
    }
  };

  handleFilterChange = (e) => {
    this.setState(
      {
        selectedFilter: e.target.checked ? "createdAt" : "filedAt",
      },
      this.fetchSelections
    );
  };

  render() {
    const { catalogue, isLoading, error } = this.state;
    if (isLoading) return <Loader />;
    if (error) return <ErrorHandler error={error} />;

    return (
      <>
        <Row className="catalogues-header-section">
          {/* <Col xs={12}>
            <Button
              variant="link"
              className="pl-0"
              onClick={() => this.props.history.goBack()}
            >
              <span className="material-icons md-18">arrow_back</span>
              Back
            </Button>
          </Col> */}
          <Col md={12} className="mt-3">
            {/* *** syndicated loan catalogue heading ( below ) *** */}
            <Col
              md={3}
              className="float-left"
              style={{ maxWidth: "23%", padding: "0px" }}
            >
              <h2> Catalogued documents for </h2>
            </Col>

            {/* *** syndicated loan drop-down ( below ) *** */}
            <Col
              md={3}
              className="float-left"
              style={{ padding: "0px", maxWidth: "11.5%" }}
            >
              {this.renderCatalogueSelector()}
            </Col>

            {/* *** Date-picker and Export button ( below ) *** */}
            <Col md={3} className="float-left" style={{ padding: "0px" }}>
              <InputGroup>
                <InputGroup.Prepend>
                  {this.renderDateSelector()}
                  <span style={{ marginLeft: "4px" }}>
                    {!!catalogue.length && (
                      <Fragment>
                        <Button
                          variant="success"
                          size="md"
                          onClick={() => this.exportFilings()}
                        >
                          Export{" "}
                          <span className="material-icons md-18">download</span>
                        </Button>{" "}
                      </Fragment>
                    )}
                  </span>
                </InputGroup.Prepend>
              </InputGroup>
            </Col>
          </Col>
          <Col md={12} className="mt-3">
            <label>
              <label className="switch">
                <input
                  type="checkbox"
                  onChange={this.handleFilterChange}
                  checked={this.state.selectedFilter === "createdAt"}
                />
                <span className="slider round"></span>
              </label>{" "}
              Filter by creation date
            </label>
          </Col>

          <hr />
        </Row>
        <Row className="catalogue-table">
          <Col xs={12} className="mt-3 mb-3">
            {this.renderDocuments()}
          </Col>
        </Row>
      </>
    );
  }

  renderCatalogueSelector() {
    const { selectedInventory, inventories } = this.state;
    const { filedAt, inventory } = this.props;

    if (selectedInventory)
      return (
        <Redirect push to={`/catalogues/${filedAt}/${selectedInventory}`} />
      );

    return (
      <div className="btn-group">
        <Dropdown>
          <Dropdown.Toggle variant="outline-primary">
            {inventories?.find((item) => item.id === inventory)?.name ||
              "Select a catalogue"}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {inventories?.map((item) => (
              <Dropdown.Item
                key={item.id}
                as="button"
                active={inventory === item.id}
                onClick={() => {
                  this.setState({ selectedInventory: item.id });
                }}
              >
                {item.name}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  }

  renderDateSelector() {
    const { filedAt, inventory } = this.props;
    const { filingDate } = this.state;

    if (filingDate)
      return <Redirect push to={`/catalogues/${filingDate}/${inventory}`} />;

    const DateInput = forwardRef(({ value, onClick }, ref) => (
      <button className="btn btn-outline-primary" onClick={onClick}>
        {value}
        <span className="material-icons">expand_more</span>
      </button>
    ));

    return (
      // <DatePicker
      //   className="btn-block"
      //   selected={moment(filedAt).toDate()}
      //   onChange={(date) =>
      //     this.setState({ filingDate: moment(date).format("YYYY-MM-DD") })
      //   }
      //   customInput={<DateInput />}
      // />
      <DatePicker
        portalId="root-portal"
        filterDate={isWeekday}
        dayClassName={() => {
          return "text-dark";
        }}
        onChange={(date) => {
          this.setState({ filingDate: moment(date).format("YYYY-MM-DD") });
        }}
        customInput={<DateInput />}
        className="btn-block bring-to-front"
        selected={moment(filedAt).toDate()}
        renderCustomHeader={({
          date,
          changeYear,
          changeMonth,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <div
            style={{ margin: 10, display: "flex", justifyContent: "center" }}
          >
            <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
              {" "}
              {"<"}{" "}
            </button>
            <select
              value={getYear(date)}
              onChange={({ target: { value } }) => changeYear(value)}
            >
              {years.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <select
              value={months[getMonth(date)]}
              onChange={({ target: { value } }) =>
                changeMonth(months.indexOf(value))
              }
            >
              {months.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <button onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
              {" "}
              {">"}{" "}
            </button>
          </div>
        )}
      />
    );
  }

  renderDocuments() {
    const { preview, isLoading, error, catalogue, selections, selectedFilter } =
      this.state;
    const { Heading } = this;
    if (isLoading) return <Loader />;
    if (error) return <ErrorHandler error={error} />;

    return (
      <Fragment>
        {catalogue.length ? (
          <Table size="sm" className="catalogue-table table-hover">
            <thead>
              <tr>
                <th />
                <Heading title="Company" classes="text-left" />
                {selectedFilter === "createdAt" && (
                  <Heading title="Filing Date" classes="text-inline" />
                )}

                <Heading title="CIK" classes="text-inline" />

                <th classes="text-inline">Film No</th>
                <Heading title="Form Type" classes="text-inline" />
                <th classes="text-inline" style={{ paddingLeft: "1%" }}>
                  Exhbit
                </th>
                <th classes="text-inline">Filing</th>
                <Heading title="Analyst" classes="text-left" />
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {catalogue.map((catalogued, index) => {
                const entity = catalogued.filing.entities[0];
                // This can be used for future
                // let filingTime = moment(catalogued.filing.filedAt)
                //   .toDate()
                //   .toLocaleTimeString("en-US", {
                //     timeZone: "America/New_York",
                //     hour: "2-digit",
                //     minute: "2-digit",
                //   });
                const fillingDate = moment(
                  catalogued?.filing?.filingDate
                ).format("MM/DD/YYYY");
                return (
                  <tr key={index}>
                    <td>{++index}</td>
                    <td title={catalogued.filing.companyName}>
                      {catalogued.filing.companyName}
                    </td>
                    {selectedFilter === "createdAt" && <td>{fillingDate}</td>}
                    <td>{catalogued.filing.cik}</td>

                    <td>{entity.filmNo}</td>
                    <td className="text-inline">
                      {catalogued.filing.formType}
                    </td>

                    <td style={{ whiteSpace: "nowrap" }}>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => {
                          this.showDocument(
                            catalogued.filing,
                            catalogued.filing.documentFormatFiles.find(
                              (item) => {
                                if (
                                  item.documentId === catalogued.documentId &&
                                  item.type === catalogued.formType
                                ) {
                                  return item;
                                }
                              }
                            )
                          );
                        }}
                      >
                        {catalogued.formType}
                      </Button>
                    </td>
                    <td className="text-inline">
                      <a
                        target="_blank"
                        rel="noreferrer"
                        href={`https://www.sec.gov/Archives/edgar/data/${
                          catalogued.filing.cik
                        }/${catalogued.filing.accessionNo.replace(/-/gi, "")}/${
                          catalogued.filing.accessionNo
                        }-index.html`}
                      >
                        <span className="material-icons">launch</span>
                      </a>
                    </td>
                    <td>
                      <span className="text-muted">
                        {getNameFromEmail(catalogued?.email)}
                      </span>
                    </td>
                    <td>
                      <Button
                        variant="warning"
                        size="sm"
                        onClick={() => {
                          this.removeFromInventory(catalogued.id);
                        }}
                      >
                        <span className="material-icons md-18">clear</span>
                      </Button>{" "}
                      <EntitiesExtraction catalogued={catalogued} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        ) : (
          <Alert variant="info">
            <h4 className="mb-0">No items to show</h4>
          </Alert>
        )}
        {preview && (
          <FilingPreview
            filing={preview.filing}
            doc={preview.doc}
            btnShow={true}
            onDocumentClosed={() => this.hideDocument()}
            catalogue={catalogue}
            selections={selections}
          />
        )}
      </Fragment>
    );
  }
}

CatalogueReview.propTypes = {};

export default withRouter(CatalogueReview);
